import { FileResponse } from "core/src/backend/responses";
import { Api, Attachment } from "../../modules";
import {
    Message,
    MessageDetail,
    MessageRecipient,
    MessageSearchCriteria,
    MessageSearchResults,
    MessageSend,
    RecipientQuery,
} from "./types";

const function_name = (name: string) => `core.messagingService.${name}`;

export const getUnreadCount = async (): Promise<number> => {
    const url = "/messages/unread/count";

    const result = await Api.url(url)
        .retries(3)
        .throwOnAbort()
        .useSignal()
        .track({ name: function_name("getUnreadCount") })
        .get<{ total: number }>();
    return result.total;
};

export const getRecipients = async (
    query: RecipientQuery
): Promise<MessageRecipient[]> => {
    if (query.leadId === undefined && query.jobId === undefined) {
        throw new Error(
            "sending a new message requires either a job id or a lead id or both."
        );
    }
    const url = "/messages/recipients";

    const result = await Api.url(url)
        .retries(3)
        .useSignal()
        .throwOnAbort()
        .track({ name: function_name("getRecipients"), data: { query } })
        .post<MessageRecipient[]>(query);
    return result;
};

export const sendMessage = async (message: MessageSend): Promise<Message> => {
    const url = "/messages";

    const result = await Api.url(url)
        .retries(0)
        .useSignal()
        .throwOnAbort()
        .track({ name: function_name("sendMessage"), data: { message } })
        .post<Message>(message);

    return result;
};

export const getMessageDetails = async (
    messageId: number
): Promise<MessageDetail> => {
    const url = `/messages/${messageId}`;
    const result = Api.url(url)
        .retries(3)
        .useSignal()
        .throwOnAbort()
        .track({
            name: function_name("getMessageDetails"),
            data: { messageId },
        })
        .get<MessageDetail>();

    return result;
};

export const getReceivedMessages = async (
    query: MessageSearchCriteria
): Promise<MessageSearchResults> => {
    const url = "/messages/inbox";
    const result = await Api.url(url)
        .retries(3)
        .useSignal()
        .throwOnAbort()
        .track({ name: function_name("getReceivedMessages"), data: { query } })
        .post<MessageSearchResults>(query);

    return result;
};

export const getSentMessages = async (
    query: MessageSearchCriteria
): Promise<MessageSearchResults> => {
    const url = "/messages/sent";
    const result = await Api.url(url)
        .retries(3)
        .useSignal()
        .throwOnAbort()
        .track({ name: function_name("getSentMessages"), data: { query } })
        .post<MessageSearchResults>(query);

    return result;
};

export const getAllMessages = async (
    query: MessageSearchCriteria
): Promise<MessageSearchResults> => {
    const url = "/messages/all";
    const result = await Api.url(url)
        .retries(3)
        .useSignal()
        .throwOnAbort()
        .track({ name: function_name("getAllMessages"), data: { query } })
        .post<MessageSearchResults>(query);

    return result;
};

export const setMessagesRead = async (messageIds: number[]): Promise<void> => {
    const url = "/messages/read";
    await Api.url(url)
        .retries(0)
        .useSignal()
        .throwOnAbort()
        .track({
            name: function_name("setMessagesUnread"),
            data: { messageIds },
        })
        .put({ messageIds });
};

export const setMessagesUnread = async (
    messageIds: number[]
): Promise<void> => {
    const url = "/messages/unread";
    await Api.url(url)
        .retries(0)
        .useSignal()
        .throwOnAbort()
        .track({
            name: function_name("setMessagesUnread"),
            data: { messageIds },
        })
        .put({ messageIds });
};

export const uploadAttachment = async (
    attachment: File,
    signal?: AbortSignal
): Promise<FileResponse> => {
    const url = "/messages/attachments";

    const attachmentData = new FormData();
    attachmentData.append("attachment", attachment);

    const result = await Api.url(url)
        .throwOnAbort()
        .useSignal(signal)
        .track({
            name: "uploadAttachment",
            data: { attachment: JSON.stringify(attachment) },
        })
        .body(attachmentData)
        .post<Attachment>();
    return result;
};

export const copyAttachments = async (
    messageId: number,
    signal?: AbortSignal
): Promise<FileResponse[]> => {
    const url = `messages/attachments/copy/${messageId}`;

    const result = await Api.url(url)
        .throwOnAbort()
        .useSignal(signal)
        .track({
            name: "copyAttachments",
            data: { messageId },
        })
        .post<FileResponse[]>();
    return result;
};

export const messageService = {
    sendMessage,
    setMessagesRead,
    setMessagesUnread,
    getSentMessages,
    getReceivedMessages,
    getMessageDetails,
    getRecipients,
    getUnreadCount,
    uploadAttachment,
    copyAttachments,
};
